/**
 * Shared comparison-card pill styling (2026-05-28).
 *
 * Single source of truth for the pill look so ReturnPolicyPills,
 * ACLCoverPill, and the inline Hero / ComparisonRow flag pills cannot
 * drift apart. Colors + geometry are an EXACT port of the signed
 * prototype `.pill` system in C:/tmp/mio-comparison-redesign.html:
 *
 *   .pill         bg rgba(255,255,255,0.05) · text #c8c8cf · border #26262d
 *   .pill.good    text #4ade80 · border rgba(74,222,128,0.30) · bg rgba(74,222,128,0.10)
 *   .pill.amber   text #fbbf24 · border rgba(251,191,36,0.30) · bg rgba(251,191,36,0.10)
 *   .pill.muted   text #8b8b94 (neutral surface)
 *   .pill.legal   text #94a3b8 · border rgba(100,116,139,0.35) · bg rgba(100,116,139,0.08)
 *                 cool slate, signals statutory / consumer-law information
 *                 visually distinct from retailer offerings (2026-06-07)
 *
 * Geometry: rounded-full, 11px, padding 4px 9px, gap 5px, nowrap.
 *
 * Hardcoded hexes (not Tailwind tokens) because the app's `accent`
 * token is a different, more-orange amber than the prototype gold and
 * the emerald/amber Tailwind ramps don't match the signed values. The
 * pills must be pixel-exact to the signed design.
 */

export type PillTone = "neutral" | "good" | "amber" | "muted" | "legal" | "danger";
export type PillSize = "sm" | "base";

// 2026-05-31 mockup audit: compact-row pills shrink to 10px/3-7 per
// .m-rank-pills .pill + .desk-rank-pills .pill. Winner-card + expanded-
// card pills stay at 11px/4-9 (base).
const SHAPE_BASE =
  "inline-flex items-center gap-[5px] whitespace-nowrap rounded-full " +
  // border-transparent (not borderless) keeps the 1px box so nothing reflows
  // and ACLCoverPill's hover:border-[...] affordance still has a width to show.
  "border border-transparent font-semibold leading-none";
const SIZE_BASE = "px-[9px] py-1 text-[11px]";
const SIZE_SM = "px-[7px] py-[3px] text-[10px]";

/**
 * Soft-fill tone COLOURS only (bg + text, theme-aware) - no geometry. The
 * single source of truth for the soft-fill palette: shared by the comparison
 * pills (via pillClass) AND the dashboard status chips (via statusPillColors),
 * so both are ALWAYS exactly the same colour. 2026-08 soft-fill (Option A).
 */
export function pillColors(tone: PillTone = "neutral"): string {
  switch (tone) {
    case "good":
      return "bg-[#D5F3E1] text-[#066A43] dark:bg-[rgba(74,222,128,0.14)] dark:text-[#5FE39B]";
    case "amber":
      return "bg-[#FBEBC6] text-[#8A5405] dark:bg-[rgba(251,191,36,0.14)] dark:text-[#FBD467]";
    case "muted":
      return "bg-[#F1EFE9] text-[#6B655C] dark:bg-white/[0.05] dark:text-[#9b968e]";
    case "legal":
      return "bg-[#E7ECF2] text-[#3E4A5C] dark:bg-[rgba(100,116,139,0.14)] dark:text-[#94a3b8]";
    case "danger":
      // Soft red for negative dashboard states (Declined / Didn't send). Same
      // soft-fill family; the comparison card doesn't use this tone today.
      return "bg-[#F7DBD5] text-[#A0341F] dark:bg-[rgba(240,135,110,0.14)] dark:text-[#F0876E]";
    case "neutral":
    default:
      return "bg-[#ECEAE3] text-[#4B463F] dark:bg-white/[0.07] dark:text-[#cfccc6]";
  }
}

// Comparison attribute pill = shape + size + soft-fill colours. Byte-identical
// output to before the pillColors extraction, so the comparison page is
// unchanged.
export function pillClass(
  tone: PillTone = "neutral",
  size: PillSize = "base",
): string {
  const sizeClass = size === "sm" ? SIZE_SM : SIZE_BASE;
  return `${SHAPE_BASE} ${sizeClass} ${pillColors(tone)}`;
}

// Dashboard status chips (My requests / watches / searches) read the SAME
// soft-fill palette as the comparison pills, so they match exactly. `color`
// is an `hsl(var(--label-*))` string; its tone maps to the pill tone.
const LABEL_TONE_TO_PILL: Record<string, PillTone> = {
  positive: "good",
  progress: "amber",
  negative: "danger",
  neutral: "neutral",
};
export function statusPillColors(labelColor: string): string {
  const t = labelColor.match(/--label-(\w+)/)?.[1] ?? "neutral";
  return pillColors(LABEL_TONE_TO_PILL[t] ?? "neutral");
}
